import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAuthStore } from '../store/authStore'
import type { UserRole } from '@/features/users/types/user'
import { UserRoleEnum } from '@/features/users/types/user'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
// import { Badge } from '@/components/ui/badge'
import { Container } from '@/components/Container'
import mccLogo from '@/assets/mcc_logo.png'

export default function RoleSelection() {
  const navigate = useNavigate()
  const { user, setActiveRole } = useAuthStore()
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  if (!user || user.roles.length < 2) {
    navigate({ to: '/' })
    return null
  }

  const handleRoleSelect = (role: UserRole) => {
    setIsLoading(true)
    try {
      setActiveRole(role)
      navigate({ to: '/' })
    } catch (error) {
      console.error('Error setting active role:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getRoleDisplayName = (role: UserRole) => {
    switch (role) {
      case UserRoleEnum.SYSTEM_ADMIN:
        return 'System Administrator'
      case UserRoleEnum.DEVELOPER:
        return 'Developer'
      case UserRoleEnum.TREASURER:
        return 'Treasurer'
      case UserRoleEnum.PASTOR:
        return 'Pastor'
      case UserRoleEnum.INVENTORY:
        return 'Inventory Manager'
      default:
        return role
    }
  }

  const getRoleDescription = (role: UserRole) => {
    switch (role) {
      case UserRoleEnum.SYSTEM_ADMIN:
        return 'Full system access across all churches'
      case UserRoleEnum.DEVELOPER:
        return 'Developer access across all churches'
      case UserRoleEnum.TREASURER:
        return `Administrative access for your church`
      case UserRoleEnum.PASTOR:
        return `Pastor access for your church`
      case UserRoleEnum.INVENTORY:
        return 'Manage inventory and supplies'
      default:
        return 'Role-based access'
    }
  }

  return (
    <Container>
      <div className="flex flex-col items-center gap-6">
        <img
          src={mccLogo}
          alt="Maranatha Christian Church"
          className="w-48 sm:w-52"
        />
        <div className="w-full max-w-2xl">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Welcome, {user.name}!</CardTitle>
              <CardDescription>
                You have multiple roles. Please select which role you'd like to
                use for this session.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {user.roles.map((role, index) => (
                <Card
                  key={index}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedRole === role
                      ? 'ring-2 ring-blue-500 bg-blue-50'
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedRole(role)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg">
                              {getRoleDisplayName(role)}
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              {getRoleDescription(role)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => selectedRole && handleRoleSelect(selectedRole)}
                  disabled={!selectedRole || isLoading}
                  className="flex-1"
                >
                  {isLoading ? 'Loading...' : 'Continue'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Container>
  )
}
